import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";

//Services
import {
  JOBTITLES_QUERY_KEY,
  jobtitleService,
} from "../../../../../Services/jobtitlesService";
import {
  JOBLEVEL_QUERY_KEY,
  joblevelService,
} from "../../../../../Services/joblevelService";
import {
  COMPANY_JOBS_QUERY_KEY,
  companyjobTitleService,
  VIEW_COMPANY_JOBS_QUERY_KEY,
} from "../../../../../Services/companyjobtitleService";

//Types
import { JobTitles } from "../../../../../Types/MasterData/JobTitles";
import { JobLevel } from "../../../../../Types/MasterData/JobLevel";
import { ViewCompanyJobs } from "../../../../../Types/CompanyProfile/CompanyJobs";

interface UpdateFormProps {
  toggle?: () => void;
  id: number;
}

const UpdateCompanyJobForm: React.FC<UpdateFormProps> = ({ toggle, id }) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    company_code: "",
    job_title_id: 0,
    job_level_id: 0,
    description: "",
    min_salary: 0.0,
    max_salary: 0.0,
  });

  const { data: companyJobData, isLoading: isFetching } = useQuery({
    queryKey: [COMPANY_JOBS_QUERY_KEY, id],
    queryFn: () => companyjobTitleService.getCompanyJobById(id),
  });

  useEffect(() => {
    if (companyJobData) {
      setFormData({
        company_code: companyJobData.company_code || "",
        job_title_id: companyJobData.job_title_id || "",
        job_level_id: companyJobData.job_level_id || "",
        description: companyJobData.description || "",
        min_salary: companyJobData.min_salary || 0.0,
        max_salary: companyJobData.max_salary || 0.0,
      });
    }
  }, [companyJobData]);

  const { data: jobtitleData, isLoading: JobTitleisLoading } = useQuery<{
    data: JobTitles[];
  }>({
    queryKey: JOBTITLES_QUERY_KEY,
    queryFn: jobtitleService.getAllJobTitles,
  });

  const jobtitles: JobTitles[] =
    jobtitleData?.data?.filter((item) => item.status === 1) ?? [];

  const { data: joblevelData, isLoading: JobLevelisLoading } = useQuery<{
    data: JobLevel[];
  }>({
    queryKey: JOBLEVEL_QUERY_KEY,
    queryFn: joblevelService.getAllJobLevel,
  });

  const joblevels: JobLevel[] =
    joblevelData?.data?.filter((item) => item.status === 1) ?? [];

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      companyjobTitleService.updateCompanyJobs(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: VIEW_COMPANY_JOBS_QUERY_KEY });
      toast.success(result.message);
      if (toggle) toggle();
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error("Failed to update job details.");
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectedJobTitle = (job_title_id: number) => {
    setFormData((prev) => ({ ...prev, job_title_id: job_title_id }));
  };

  const handleSelectedJobLevel = (job_level_id: number) => {
    setFormData((prev) => ({ ...prev, job_level_id: job_level_id }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { min_salary, max_salary } = formData;

    if ((min_salary && !max_salary) || (!min_salary && max_salary)) {
      toast.error(
        "Both Min Salary and Max Salary are required if one is filled.",
      );
      return;
    }

    if (min_salary && max_salary && Number(min_salary) > Number(max_salary)) {
      toast.error("Min Salary cannot be greater than Max Salary.");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }
    updateMutation.mutate(formData);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Job Title<b className="text-danger">*</b>
            </Label>
            <Input
              value={formData.job_title_id}
              id="job_title_id"
              name="job_title_id"
              type="select"
              disabled={JobTitleisLoading || isFetching}
              onChange={(e) => handleSelectedJobTitle(Number(e.target.value))}
            >
              <option value="0" disabled>
                {JobTitleisLoading ? "Loading..." : "Select Job Title"}
              </option>
              {!JobTitleisLoading &&
                jobtitles.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.job_title}
                  </option>
                ))}
            </Input>
          </Col>
          <Col md="12">
            <Label>
              Job Level<b className="text-danger">*</b>
            </Label>
            <Input
              value={formData.job_level_id}
              id="job_level_id"
              name="job_level_id"
              type="select"
              disabled={JobLevelisLoading || isFetching}
              onChange={(e) => handleSelectedJobLevel(Number(e.target.value))}
            >
              <option value="0" disabled>
                {JobLevelisLoading ? "Loading..." : "Select Job Level"}
              </option>
              {!JobLevelisLoading &&
                joblevels.map((item) => (
                  <option key={item.id} value={item.id}>
                    {`Lvl ${item.level_rank} - ${item.name}`}
                  </option>
                ))}
            </Input>
          </Col>
          <Col md="6">
            <Label>Min Salary</Label>
            <Input
              id="min_salary"
              name="min_salary"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.min_salary ?? ""}
              onChange={handleInputChange}
              required={!!formData.max_salary}
              disabled={isFetching}
            />
          </Col>
          <Col md="6">
            <Label>Max Salary</Label>
            <Input
              id="max_salary"
              name="max_salary"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.max_salary ?? ""}
              onChange={handleInputChange}
              required={!!formData.min_salary}
              disabled={isFetching}
            />
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={updateMutation.isPending || isFetching}
            >
              {updateMutation.isPending ? "Updating Cluster..." : "Update"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateCompanyJobForm;
